import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import { Tooltip } from "react-tooltip";
import Select, { components } from "react-select";
import { format, isValid } from "date-fns";
import { BASE_URL } from "../../config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faSyncAlt,
  faAngleDoubleLeft,
  faAngleLeft,
  faAngleRight,
  faAngleDoubleRight,
  faTableList,
  faCalendar,
  faHashtag,
  faUser,
  faBuilding,
  faHospital,
  faShieldAlt,
  faUserTie,
  faCheckCircle,
  faClock,
  faInfoCircle,
  faEllipsisVertical,
  faPlus,
  faSpinner,
  faFilter,
  faSort,
  faSortUp,
  faSortDown,
  faClipboardCheck,
  faBed,
} from "@fortawesome/free-solid-svg-icons";
import Type3ContinueModal from "./Type3ContinueModal";

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  loading,
  itemsPerPage,
  totalCount,
}) => {
  const renderPageNumbers = () => {
    const pages = [];
    const delta = 2;
    const range = [];
    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }
    if (currentPage - delta > 2) range.unshift("...");
    if (currentPage + delta < totalPages - 1) range.push("...");
    pages.push(1);
    pages.push(...range);
    if (totalPages > 1) pages.push(totalPages);
    return pages.map((p, i) => (
      <button
        key={i}
        onClick={() => typeof p === "number" && onPageChange(p)}
        disabled={p === "..."}
        className={`p-2 px-3 text-sm font-medium rounded-lg transition-all duration-200 ${
          currentPage === p
            ? "bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/25"
            : "text-gray-700 hover:bg-gray-100 border border-gray-200"
        } ${p === "..." ? "opacity-50 cursor-default" : "hover:scale-105"}`}
      >
        {p}
      </button>
    ));
  };
  return (
    <div className="flex flex-col md:flex-row md:items-center justify-between p-6 border-t border-gray-100 bg-white/50 backdrop-blur-sm">
      <div className="text-sm text-gray-600 mb-3 md:mb-0">
        Showing{" "}
        <span className="font-semibold text-gray-900">
          {(currentPage - 1) * itemsPerPage + 1}
        </span>{" "}
        to{" "}
        <span className="font-semibold text-gray-900">
          {Math.min(currentPage * itemsPerPage, totalCount)}
        </span>{" "}
        of <span className="font-semibold text-gray-900">{totalCount}</span>{" "}
        results
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1 || loading}
          className="p-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-30 transition-all duration-200 hover:scale-105"
          aria-label="First page"
        >
          <FontAwesomeIcon icon={faAngleDoubleLeft} className="text-sm" />
        </button>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1 || loading}
          className="p-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-30 transition-all duration-200 hover:scale-105"
          aria-label="Previous page"
        >
          <FontAwesomeIcon icon={faAngleLeft} className="text-sm" />
        </button>
        <div className="flex items-center gap-1 px-2 py-1 bg-gray-50 rounded-lg">
          {renderPageNumbers()}
        </div>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages || loading}
          className="p-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-30 transition-all duration-200 hover:scale-105"
          aria-label="Next page"
        >
          <FontAwesomeIcon icon={faAngleRight} className="text-sm" />
        </button>
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages || loading}
          className="p-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-30 transition-all duration-200 hover:scale-105"
          aria-label="Last page"
        >
          <FontAwesomeIcon icon={faAngleDoubleRight} className="text-sm" />
        </button>
      </div>
    </div>
  );
};

const safeFilterOption = (option, inputValue) => {
  try {
    const searchText =
      typeof inputValue === "string" ? inputValue : String(inputValue || "");
    if (!searchText.trim()) return true;

    const label = option.label || option.data?.label || "";
    return String(label).toLowerCase().includes(searchText.toLowerCase());
  } catch (error) {
    return true;
  }
};

const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

const AdmissionVerificationTable = () => {
  const navigate = useNavigate();

  const [showVocModal, setShowVocModal] = useState(false);
  const [selectedTransNo, setSelectedTransNo] = useState(null);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    status: [],
    start_date: "",
    end_date: "",
  });
  const [sortConfig, setSortConfig] = useState({
    key: "verification_date",
    direction: "desc",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [filterLoading, setFilterLoading] = useState({
    status: false,
  });
  const [statusSearch, setStatusSearch] = useState("");

  const columns = [
    {
      key: "transactions_no",
      label: "Transactions #",
      sortable: true,
      icon: faHashtag,
      width: "w-40",
    },
    {
      key: "member_name",
      label: "Member Name",
      sortable: true,
      icon: faUser,
      width: "w-64",
    },
    {
      key: "corp_name",
      label: "Corporate Name",
      sortable: true,
      icon: faBuilding,
      width: "w-64",
    },
    {
      key: "plan_name",
      label: "Plan Name",
      sortable: true,
      icon: faShieldAlt,
      width: "w-32",
    },
    {
      key: "provider_name",
      label: "Provider Name",
      sortable: true,
      icon: faHospital,
      width: "w-64",
    },
    {
      key: "coverage",
      label: "Coverage",
      sortable: true,
      icon: faShieldAlt,
      width: "w-32",
    },
    {
      key: "verification_date",
      label: "Verification Date",
      sortable: true,
      icon: faCalendar,
      width: "w-48",
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      icon: faUserTie,
      width: "w-40",
    },
    {
      key: "actions",
      label: "Action",
      sortable: false,
      icon: faEllipsisVertical,
      width: "w-32",
    },
  ];

  const statusMap = {
    "Approved TRN": {
      color: "bg-green-100 text-green-800 border border-green-200",
      icon: faCheckCircle,
      gradient: "from-green-400 to-emerald-500",
    },
    "Approved TRN with LOA": {
      color: "bg-blue-100 text-blue-800 border border-blue-200",
      icon: faInfoCircle,
      gradient: "from-blue-400 to-indigo-500",
    },
    Discharged: {
      color: "bg-purple-100 text-purple-800 border border-purple-200",
      icon: faCheckCircle,
      gradient: "from-purple-400 to-violet-500",
    },
    Pending: {
      color: "bg-yellow-100 text-yellow-800 border border-yellow-200",
      icon: faClock,
      gradient: "from-yellow-400 to-amber-500",
    },
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "48px",
      backgroundColor: "white",
      borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(59, 130, 246, 0.1)" : "none",
      color: "#374151",
      "&:hover": { borderColor: "#3b82f6" },
      fontSize: "14px",
    }),
    input: (provided) => ({
      ...provided,
      color: "#374151",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#9ca3af",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#1f2937",
      fontWeight: "500",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#3b82f6"
        : state.isFocused
        ? "#f3f4f6"
        : "white",
      color: state.isSelected ? "white" : "#374151",
      ":active": {
        ...provided[":active"],
        backgroundColor: state.isSelected ? "#3b82f6" : "#f3f4f6",
        color: state.isSelected ? "white" : "#374151",
      },
      fontSize: "14px",
      padding: "12px 16px",
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: "white",
      boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
      borderRadius: "8px",
      marginTop: "4px",
    }),
    noOptionsMessage: (provided) => ({
      ...provided,
      color: "#6b7280",
      fontSize: "14px",
    }),
    loadingMessage: (provided) => ({
      ...provided,
      color: "#3b82f6",
      fontSize: "14px",
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#3b82f6",
      color: "white",
    }),
  };

  const LoadingIndicator = (props) => (
    <components.LoadingIndicator {...props}>
      <FontAwesomeIcon icon={faSpinner} spin className="text-indigo-500 mr-2" />
    </components.LoadingIndicator>
  );

  const staticStatusOptions = [
    { value: "01", label: "Approved TRN" },
    { value: "02", label: "Approved TRN with LOA" },
    { value: "03", label: "Discharged" },
  ];

  const debouncedStatusSearch = useCallback(
    debounce(async (query) => {
      if (query.length < 2) return;
      setFilterLoading((prev) => ({ ...prev, status: true }));
      setFilterLoading((prev) => ({ ...prev, status: false }));
    }, 300),
    []
  );

  const fetchFilterOptions = async () => {
    setStatusOptions(staticStatusOptions);
    setFilterLoading((prev) => ({
      ...prev,
      status: false,
    }));
  };

  useEffect(() => {
    debouncedStatusSearch(statusSearch);
  }, [statusSearch, debouncedStatusSearch]);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchData();
  }, [filters, sortConfig, currentPage, itemsPerPage, searchTerm]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: itemsPerPage,
        search: searchTerm,
        status: filters.status.map((s) => s.value).join(","),
        from_date: filters.start_date,
        to_date: filters.end_date,
        sort_by: sortConfig.key,
        sort_dir: sortConfig.direction,
      };
      const response = await axios.get(`${BASE_URL}/admission-verifications`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem(
            "authentication/token"
          )}`,
        },
      });

      if (response.data.status !== "success") {
        toast.error(response.data.message || "Failed to fetch data");
        return;
      }

      setData(response.data.data.data || []);
      setTotalCount(response.data.data.total || 0);
    } catch (error) {
      if (error.response?.status === 401) window.location.href = "/login";
      if (error.response?.status === 403)
        toast.error("You do not have permission to access this.");
      if (error.response?.status === 429)
        toast.error("Rate limit exceeded. Please try again later.");
      toast.error(
        error.response?.data?.message ||
          "Failed to fetch admission verifications."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction:
        sortConfig.key === key && sortConfig.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  const handleFilterChange = (key, value) => {
    if (key === "status") {
      setFilters((prev) => ({
        ...prev,
        [key]: Array.isArray(value) ? value : [],
      }));
    } else {
      setFilters((prev) => ({ ...prev, [key]: value }));
    }
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const clearFilters = () => {
    setFilters({
      status: [],
      start_date: "",
      end_date: "",
    });
    setSearchTerm("");
    setCurrentPage(1);
    setStatusSearch("");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return isValid(date) ? format(date, "yyyy-MM-dd HH:mm") : "-";
  };

  const LoadingSkeleton = ({ rows, columns }) => (
    <tbody className="bg-white divide-y divide-gray-200">
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={i} className="hover:bg-gray-50 animate-pulse">
          {Array.from({ length: columns }).map((_, j) => (
            <td key={j} className="px-4 py-4">
              <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-full" />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );

  const handleStatusInputChange = (inputValue) => {
    setStatusSearch(inputValue || "");
    return inputValue;
  };

  const handlePerformVoc = (row) => {
    setSelectedTransNo(row.transactions_no);
    setShowVocModal(true);
  };

  const EmptyState = ({
    title = "No admission verifications found",
    subtitle = "Try adjusting your search or filters.",
  }) => (
    <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-200 bg-gradient-to-br from-gray-50 to-blue-50/30 p-12 text-center">
      <div className="mb-4 rounded-full bg-indigo-100 p-4">
        <FontAwesomeIcon
          icon={faTableList}
          className="h-8 w-8 text-indigo-600"
        />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-500 mb-6 max-w-sm">{subtitle}</p>
      <button
        onClick={() => navigate("/generate-loa-transaction")}
        className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105"
      >
        <FontAwesomeIcon icon={faPlus} className="mr-2" />
        New Admission
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2">
      <div className="px-4 py-4 border-b border-gray-200 bg-white/95 backdrop-blur-sm">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg shadow-md">
              <FontAwesomeIcon icon={faBed} className="text-white text-lg" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">
                In-Patient/Maternity Admission Verification
              </h1>
              <p className="text-gray-500 text-xs mt-0.5">
                Continue VOC verification for admission transactions
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate("/generate-loa-transaction")}
            className="inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium rounded-lg hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 transition-all duration-200 shadow-md hover:shadow-lg text-sm"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2 text-xs" />
            New Admission
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-xs border border-gray-100 p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs"
            />
            <input
              type="text"
              placeholder="Search transactions, members, corporates..."
              value={searchTerm}
              onChange={handleSearch}
              className="pl-9 pr-3 py-2.5 w-full border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all bg-white text-sm"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-3 py-2.5 bg-white border border-gray-200 text-gray-600 font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-200 text-sm shadow-xs"
            >
              <FontAwesomeIcon icon={faFilter} className="mr-1.5 text-xs" />
              Filters
            </button>
            <button
              onClick={clearFilters}
              className="inline-flex items-center px-3 py-2.5 bg-white border border-gray-200 text-gray-600 font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-200 text-sm shadow-xs"
            >
              Clear
            </button>
            <button
              onClick={fetchData}
              disabled={loading}
              className="inline-flex items-center px-3 py-2.5 bg-gradient-to-r from-gray-600 to-gray-700 text-white font-medium rounded-lg hover:from-gray-700 hover:to-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all duration-200 shadow-sm disabled:opacity-50 text-sm"
            >
              <FontAwesomeIcon
                icon={faSyncAlt}
                className={`mr-1.5 text-xs ${loading ? "animate-spin" : ""}`}
              />
              Refresh
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 p-3 bg-gray-50/80 rounded-lg border border-gray-100 mt-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700 block">
                Status
              </label>
              <Select
                isMulti
                placeholder="Select statuses..."
                value={filters.status}
                onChange={(selected) => handleFilterChange("status", selected)}
                options={statusOptions}
                isLoading={filterLoading.status}
                inputValue={statusSearch}
                onInputChange={handleStatusInputChange}
                styles={customStyles}
                components={{ LoadingIndicator }}
                isClearable
                filterOption={safeFilterOption}
                className="text-xs"
              />
            </div>

            <div className="space-y-1 md:col-span-2">
              <label className="text-xs font-medium text-gray-700 block">
                Date Range (Verification)
              </label>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) =>
                    handleFilterChange("start_date", e.target.value)
                  }
                  className="w-full px-2 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent transition-all bg-white text-xs"
                />
                <input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) =>
                    handleFilterChange("end_date", e.target.value)
                  }
                  className="w-full px-2 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent transition-all bg-white text-xs"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700 block">
                Items per page
              </label>
              <select
                value={itemsPerPage}
                onChange={(e) =>
                  handleItemsPerPageChange(Number(e.target.value))
                }
                className="w-full px-2 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent transition-all bg-white text-gray-900 text-xs"
              >
                {[5, 10, 20, 50, 100].map((val) => (
                  <option key={val} value={val}>
                    Show {val}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mt-2">
          <div className="overflow-x-auto relative">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100/50">
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className={`px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider transition-colors ${column.width}`}
                    >
                      <div className="flex items-center">
                        <FontAwesomeIcon
                          icon={column.icon}
                          className="mr-2 text-gray-400"
                        />
                        <span>{column.label}</span>
                        {column.sortable && (
                          <button
                            onClick={() => handleSort(column.key)}
                            className="ml-2 p-1 hover:bg-gray-200 rounded transition-colors"
                          >
                            <FontAwesomeIcon
                              icon={
                                sortConfig.key === column.key
                                  ? sortConfig.direction === "asc"
                                    ? faSortUp
                                    : faSortDown
                                  : faSort
                              }
                              className={`text-sm ${
                                sortConfig.key === column.key
                                  ? "text-indigo-600"
                                  : "text-gray-400"
                              }`}
                            />
                          </button>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              {loading ? (
                <LoadingSkeleton rows={itemsPerPage} columns={columns.length} />
              ) : (
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="px-6 py-12">
                        <EmptyState />
                      </td>
                    </tr>
                  ) : (
                    data.map((row, idx) => (
                      <tr
                        key={idx}
                        className="group hover:bg-gradient-to-r hover:from-indigo-50/30 hover:to-purple-50/30 transition-all duration-200 cursor-pointer"
                      >
                        <td className="px-4 py-4 text-sm font-semibold text-gray-900 bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-l">
                          {row.transactions_no || "-"}
                        </td>
                        <td className="px-4 py-4 text-sm font-medium text-gray-900">
                          {row.member_name || "-"}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700">
                          {row.corp_name || "-"}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700">
                          {row.plan_name || "-"}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700">
                          {row.provider_name || "-"}
                        </td>
                        <td className="px-4 py-4 text-sm">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {row.coverage || "-"}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700">
                          {formatDate(row.verification_date)}
                        </td>
                        <td className="px-4 py-4 text-sm">
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 ${
                              statusMap[row.status]?.color ||
                              "bg-gray-100 text-gray-800 border border-gray-200"
                            }`}
                          >
                            <FontAwesomeIcon
                              icon={statusMap[row.status]?.icon || faInfoCircle}
                              className="mr-1.5 text-xs"
                            />
                            {row.status || "-"}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm font-medium">
                          <button
                            onClick={() => handlePerformVoc(row)}
                            className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:from-indigo-600 hover:to-purple-600 shadow-md"
                          >
                            <FontAwesomeIcon
                              icon={faClipboardCheck}
                              className="mr-1"
                            />
                            Perform VoC
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              )}
            </table>
          </div>
          {data.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              loading={loading}
              itemsPerPage={itemsPerPage}
              totalCount={totalCount}
            />
          )}
        </div>
      </div>

      <Type3ContinueModal
        isOpen={showVocModal}
        onClose={() => {
          setShowVocModal(false);
          setSelectedTransNo(null);
          fetchData();
        }}
        transNo={selectedTransNo}
      />

      <Tooltip id="tooltip" />
    </div>
  );
};

export default AdmissionVerificationTable;
